import React, { FC, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { BoxV2 as Box, Flex, TextInput } from 'portal-commons';
import { ContentBlockHeader, RadioButton, Checkbox } from '@/components';
import { faGlobe, faCircleNodes } from '@fortawesome/pro-regular-svg-icons';
import Divider from './Divider';
import StepFooter from './StepFooter';
import useRegisterControllers from '../hooks/useRegisterControllers';
import { CspRegistrationFormData } from '../types';
import { S3_ASSETS_PATH } from '@/constants';

const CountryOptions = [
  {
    name: 'United States',
    code: 'US',
    flag: `${S3_ASSETS_PATH}/images/flags/us.svg`,
  },
  {
    name: 'Germany',
    code: 'DE',
    flag: `${S3_ASSETS_PATH}/images/flags/de.v2.svg`,
  },
  // { name: 'Mexico', code: 'MX', flag: '🇲🇽' },
];

const MarketSelectorCard: FC<{
  name: string;
  code: string;
  flag: string;
  selected?: boolean;
  onClick: (code: string) => void;
}> = ({ name, flag, selected, onClick, code }) => {
  return (
    <Flex
      data-testid={`marketSelector-${code}`}
      sx={{
        flexDirection: 'column',
        flex: '1 1 0',
        maxWidth: 394,
        height: 172,
        padding: '24px 32px',
        alignItems: 'center',
        justifyContent: 'space-between',
        border: `2px solid ${selected ? '#D41C62' : '#E5E7EB'}`,
        borderRadius: 14,
        cursor: 'pointer',
      }}
      onClick={() => onClick(code)}
    >
      <Box
        as="img"
        src={flag}
        alt={`${code} flag`}
        sx={{ height: 88, width: 'auto' }}
      />
      <Box
        sx={{
          fontFamily: 'Roboto',
          fontSize: 24,
          fontWeight: 400,
          color: '#081F2D',
          lineHeight: 'normal',
        }}
      >
        {name}
      </Box>
    </Flex>
  );
};

const StepRegister: FC = () => {
  const {
    control,
    formState: { isValid, isValidating },
  } = useFormContext<CspRegistrationFormData>();

  const {
    market,
    setMarket,
    primaryCnpName,
    setPrimaryCnpName,
    validatePrimaryCnpName,
    primaryCnpNameError,
    relationshipLength,
    setRelationshipLength,
    validateRelationshipLength,
    relationshipLengthError,
    has2ndCnp,
    setHas2ndCnp,
    secondaryCnpName,
    setSecondaryCnpName,
    validateSecondaryCnpName,
    secondaryCnpNameError,
    secondaryRelationshipLength,
    setSecondaryRelationshipLength,
    validateSecondaryRelationshipLength,
    secondaryRelationshipLengthError,
    confirmCsp,
    setConfirmCsp,
  } = useRegisterControllers(control);

  const isNextDisabled = isValidating || !isValid;

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Scroll to the bottom of the container when a secondary CNP is selected
    if (has2ndCnp === 'yes' && scrollContainerRef.current) {
      setTimeout(() => {
        scrollContainerRef.current?.scrollTo({
          top: scrollContainerRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }, 100);
    }
  }, [has2ndCnp]);

  return (
    <Flex sx={{ flexDirection: 'column', height: '100%' }}>
      <Box ref={scrollContainerRef} sx={{ flex: 1, overflowY: 'auto', px: 24 }}>
        <ContentBlockHeader
          icon={faGlobe}
          title="Countries"
          subTitle="Select your market:"
        />
        <Flex sx={{ gap: 16 }}>
          {CountryOptions.map((option) => (
            <MarketSelectorCard
              key={option.code}
              {...option}
              selected={option.code === market}
              onClick={setMarket}
            />
          ))}
        </Flex>

        <Divider sx={{ my: 24 }} />

        <ContentBlockHeader
          icon={faCircleNodes}
          title="Connectivity Partner"
          subTitle="Who is your Connectivity Partner (CNP) or Direct Connect Aggregator (DCA) for SMS Delivery?"
        />
        <Flex sx={{ my: 24, gap: 24, alignItems: 'flex-start' }}>
          <TextInput
            label="CNP/DCA"
            required
            maxLength={100}
            style={{ flexGrow: 1 }}
            value={primaryCnpName}
            onChange={setPrimaryCnpName}
            onBlur={validatePrimaryCnpName}
            error={!!primaryCnpNameError}
            note={primaryCnpNameError?.message}
            data-testid="questionnairePrimaryCnpNameTextInput"
          />
          <TextInput
            label="Length of existing relationship"
            required
            maxLength={1000}
            style={{ flexGrow: 1 }}
            value={relationshipLength}
            onChange={setRelationshipLength}
            onBlur={validateRelationshipLength}
            error={!!relationshipLengthError}
            note={
              relationshipLengthError?.message ||
              'How long have you been working together'
            }
            data-testid="questionnaireRelationshipLengthTextInput"
          />
        </Flex>

        <Flex sx={{ flexDirection: 'column', gap: 24, pb: 24 }}>
          <Flex sx={{ flexDirection: 'column', gap: 16 }}>
            Do you have a secondary CNP or DCA?
            <Flex
              sx={{ flexDirection: 'row', gap: 'xxl' }}
              data-testid="secondaryConnectivityPartnerDetailsRadioButtonList"
            >
              <RadioButton
                label="No"
                value="no"
                checked={has2ndCnp === 'no'}
                onChange={setHas2ndCnp}
              />
              <RadioButton
                label="Yes"
                value="yes"
                checked={has2ndCnp === 'yes'}
                onChange={setHas2ndCnp}
              />
            </Flex>
          </Flex>

          {has2ndCnp === 'yes' && (
            <Flex sx={{ flexDirection: 'column', gap: 16 }}>
              Please list your secondary Connectivity Partner (CNP) or Direct
              Connect Aggregator (DCA) below.
              <Flex sx={{ gap: 24, alignItems: 'flex-start' }}>
                <TextInput
                  label="CNP/DCA"
                  required
                  style={{ flexGrow: 1 }}
                  maxLength={100}
                  value={secondaryCnpName}
                  onChange={setSecondaryCnpName}
                  onBlur={validateSecondaryCnpName}
                  error={!!secondaryCnpNameError}
                  note={secondaryCnpNameError?.message}
                  data-testid="secondaryConnectivityPartnerDetailsTextInput"
                />
                <TextInput
                  label="Relationship Length"
                  required
                  maxLength={1000}
                  style={{ flexGrow: 1 }}
                  value={secondaryRelationshipLength}
                  onChange={setSecondaryRelationshipLength}
                  onBlur={validateSecondaryRelationshipLength}
                  error={!!secondaryRelationshipLengthError}
                  note={
                    secondaryRelationshipLengthError?.message ||
                    'How long have you been working together'
                  }
                  data-testid="secondaryRelationshipLengthTextInput"
                />
              </Flex>
            </Flex>
          )}
        </Flex>
      </Box>

      <StepFooter nextDisabled={isNextDisabled} nextLabel="Next: CSP Details">
        <Checkbox
          data-testid="isCspConfirmCheckbox"
          label="I confirm that I operate as a Campaign Service Provider (CSP) offering a Communications Platform as a Service (CPaaS), A2P services, or SMS campaign services."
          checked={confirmCsp}
          onChange={setConfirmCsp}
        />
      </StepFooter>
    </Flex>
  );
};

export default StepRegister;
